import React, { useContext } from "react";
import { styled, Theme, CSSObject } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { sidebarItems, sidebarLast, sidebarUserGuide } from "../../constant/Sidebar";
import { useLocation, useNavigate } from "react-router-dom";
import { RouterList } from "../../router/Router";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { handleLogoutRedux } from "../../redux/authReducer";
import TourContext from "../../context/TourContext";
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import "./Sidebar.scss";

const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
  marginTop: "80px",
});


const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  overflowY: "hidden",

  width: `calc(${theme.spacing(7)} + 1px)`,
  marginTop: "80px",

  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
    marginTop: "80px",
    boxShadow: "2px 0px 10px 0px #77777733",
  },
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  marginTop: "80px",

  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
    '& .MuiListItemText-primary': {
      display: 'block !important'
    }
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
    '& .MuiListItemText-primary': {
      display: 'none !important'
    }
  }),
}));

export default function MiniDrawer() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { userDetail, userPermissions }: any = useAppSelector((state) => state.auth);
  const { pathname } = useLocation();
  const { setState, setStepIndex }: any = useContext(TourContext);

  const [open, setOpen] = React.useState(false);
  const [openIndex, setOpenIndex] = React.useState<number | null>(null);

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleListItemClick = (item: any, index: number) => {
    if (item.subMenus) {
      setOpenIndex((prevIndex: any) => (prevIndex === index ? null : index));
    } else {
      navigate(item.path?.replace(':id', userDetail.userId));
      handleDrawerClose();
    }
  };


  const isItemActive = (item: any) => {
    if (item.path) {
      // If the item's path is present, check if the base path is part of the current location
      const basePath = item.path.split('/:')[0]; // Split on dynamic segment
      return location.pathname.includes(basePath);
    } else if (item.subMenus) {
      // Check if any of the subMenus' base paths are part of the current location
      return item.subMenus.some((subItem: any) => {
        const subItemBasePath = subItem.path.split('/:')[0];
        return location.pathname.includes(subItemBasePath);
      });
    }
    return false;
  };


  const handleLogout = () => {
    dispatch(handleLogoutRedux());
    navigate(RouterList[0].path);
  };

  const handelUserGuide = () => {
    if (pathname.includes('create') || pathname.includes('update') || pathname.includes('view')) {
      setState((prev: any) => ({ ...prev, run: true, accordionOpen: true, isRestart: true }));
      setStepIndex(10);
    } else if (pathname === '/estimates') {
      setState((prev: any) => ({ ...prev, run: true, accordionOpen: false, isRestart: true }));
      setStepIndex(0);
    }
  }
  // const handleArrowIcon = () => {
  //   if (subOpen) {
  //     return <KeyboardArrowUpIcon sx={{ fontSize: "25px", marginTop: 1, marginRight: 3 }} />
  //   }
  //   else {
  //     return <KeyboardArrowDownIcon sx={{ fontSize: "25px", marginTop: 1, marginRight: 4 }} />
  //   }
  // }
  const handleArrowIcon = (index: number) => {
    return openIndex === index
      ? <KeyboardArrowUpIcon sx={{ fontSize: 25, mt: 1, mr: 3 }} />
      : <KeyboardArrowDownIcon sx={{ fontSize: 25, mt: 1, mr: 4 }} />;
  };
  return (
    <Box data-testid="sidebar">
      <Drawer
        data-testid="sidebar-Drawer"
        variant="permanent"
        open={open}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        anchor="left"
        className="sidebar"
      >
        <Box sx={{ marginTop: "30px", marginBottom: "10px" }} className="my-sidebar">
          <List data-testid="Expand">
            {sidebarItems?.filter((elm: any) => {
              if (elm.isCheckUserPermissionId) {
                if (userPermissions && userPermissions?.find((permission: any) => {
                  return elm.isCheckUserPermissionId.includes(+permission.pId);
                })) {
                  return true;
                } else {
                  return false;
                }
              }
              return true
            }).map(
              (item: any, index: number) =>
                item.roleId.includes(userDetail?.roleId) && (
                  <ListItem
                    key={item.label + index + Math.random()}
                    disablePadding
                    sx={{
                      display: "block",
                      color: isItemActive(item) ? "#4E4FA9" : " #B8B9DD",
                    }}
                    className={item.label.replace(' ', '_')}
                  >
                    {open && (
                      <ListItemText primary={item.Text} className="text" />
                    )}
                    <ListItemButton
                      key={index}
                      onClick={() => handleListItemClick(item, index)}
                    >
                      <ListItemIcon sx={{ minWidth: "48px !important" }}>
                        {isItemActive(item) ? item.activeIcon : item.icon}
                      </ListItemIcon>

                      <ListItemText
                        primary={item.label}
                        sx={{
                          fontSize: "19px",
                          fontWeight: "600",
                          lineHeight: "22px",
                        }}
                      />
                     {open && <span>{item.IsDropdown && handleArrowIcon(index)}</span>}
                    </ListItemButton>
                    {item.subMenus && openIndex === index && (
                      <List sx={{ pl: 4 }}>
                        {item.subMenus.map((subItem: any, subIndex: number) => (
                          <ListItem key={subItem.label + subIndex} disablePadding sx={{ display: "block", color: isItemActive(subItem) ? "#4E4FA9" : " #B8B9DD" }}>
                            <ListItemButton onClick={() => handleListItemClick(subItem, subIndex)} sx={{ pl: 2 }}>
                              <ListItemText primary={subItem.label} sx={{ fontSize: "16px", fontWeight: "500" }} />
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </ListItem>
                )
            )}
          </List>
        </Box>

        <Box sx={{ marginBottom: "100px" }}>
          <List>
            {sidebarUserGuide.map((item, index) => (
              <ListItem
                key={item.label + index + Math.random()}
                disablePadding
                sx={{ display: "block" }}
                className={"step_by"}

              >
                <ListItemButton onClick={handelUserGuide} key={index}>
                  <ListItemIcon sx={{ minWidth: "48px !important" }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    sx={{ color: "#B8B9DD" }}
                    className="step-by-step"
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          <List sx={{ mt: 2, ml: 0.2 }}>
            {sidebarLast.map((item, index) => (
              <ListItem
                key={item.label + index + Math.random()}
                disablePadding
                sx={{ display: "block" }}
              >
                <ListItemButton onClick={handleLogout} key={index}>
                  <ListItemIcon sx={{ minWidth: "48px !important" }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    sx={{ color: "#B8B9DD" }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>
    </Box>
  );
}
